import { useState, useRef, useEffect, type ReactElement } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

interface Tool {
  name: string
  path: string
  icon: ReactElement
}

const tools: Tool[] = [
  {
    name: 'Test Case Generator',
    path: '/test-case/',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
        <rect x="3" y="2" width="10" height="12" rx="1.5" />
        <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: 'Offers Audit',
    path: '/offers-audit/',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
        <circle cx="8" cy="8" r="5.5" />
        <circle cx="8" cy="8" r="2" />
        <path d="M8 2.5v1M8 12.5v1M2.5 8h1M12.5 8h1" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: 'Risk Prioritization',
    path: '/risk-prioritization/',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
        <path d="M3 12h2v2H3zM7 8h2v6H7zM11 4h2v10h-2z" fill="currentColor" />
      </svg>
    ),
  },
  {
    name: 'RT Audit',
    path: '/rt-audit/',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
        <rect x="2" y="3" width="12" height="10" rx="1.5" />
        <path d="M2 6h12M5 6v7" strokeLinecap="round" />
        <path d="M7.5 8.5l1.5 1.5 3-3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
]

interface HeaderProps {
  showToolSwitcher?: boolean
}

export default function Header({ showToolSwitcher = false }: HeaderProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const toolsMenuRef = useRef<HTMLDivElement>(null)
  const profileMenuRef = useRef<HTMLDivElement>(null)

  const getCurrentTool = (): Tool | null => {
    return tools.find((tool) => location.pathname.startsWith(tool.path)) || null
  }

  const currentTool = getCurrentTool()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target as Node)) {
        setToolsMenuOpen(false)
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false)
      }
    }
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setToolsMenuOpen(false)
        setProfileMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeydown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  const handleToolSwitch = (path: string) => {
    navigate(path)
    setToolsMenuOpen(false)
  }

  const getUserInitials = (): string => {
    if (!user) return '?'
    if (user.givenName && user.familyName) {
      return user.givenName.charAt(0).toUpperCase() + user.familyName.charAt(0).toUpperCase()
    } else if (user.name) {
      const nameParts = user.name.split(' ')
      if (nameParts.length >= 2) {
        return nameParts[0].charAt(0).toUpperCase() + nameParts[nameParts.length - 1].charAt(0).toUpperCase()
      }
      return user.name.charAt(0).toUpperCase()
    }
    return user.email.charAt(0).toUpperCase()
  }

  return (
    <nav className="h-[52px] bg-linear-bg-sidebar border-b border-linear-border">
      <div className="h-full max-w-[1400px] mx-auto px-5 flex items-center justify-between">
        {/* Left: Brand + Tool Switcher */}
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-2.5 group"
          >
            <div className="w-[18px] h-[18px] bg-mustard rounded flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-semibold text-white">FD</span>
            </div>
            <span className="text-[13px] font-medium text-linear-text group-hover:text-linear-accent transition-colors">
              Five & Done QA
            </span>
          </Link>

          {/* Tool Switcher (Desktop) */}
          {showToolSwitcher && currentTool && (
            <div className="hidden md:flex items-center gap-2">
              <span className="text-linear-text-placeholder">/</span>
              <div className="relative" ref={toolsMenuRef}>
                <button
                  onClick={() => setToolsMenuOpen(!toolsMenuOpen)}
                  className="flex items-center gap-2 px-2 py-1 rounded text-[13px] font-medium text-linear-text-secondary hover:bg-linear-text-muted/10 hover:text-linear-text transition-colors"
                  aria-expanded={toolsMenuOpen}
                  aria-haspopup="true"
                >
                  <span className="text-linear-text-muted">{currentTool.icon}</span>
                  <span>{currentTool.name}</span>
                  <svg
                    className={`w-3.5 h-3.5 text-linear-text-muted transition-transform duration-150 ${toolsMenuOpen ? 'rotate-180' : ''}`}
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                {toolsMenuOpen && (
                  <div className="absolute left-0 mt-1 w-56 bg-linear-bg-card border border-linear-border-input rounded-lg shadow-lg overflow-hidden z-50 animate-scale-in">
                    {tools.map((tool) => {
                      const isActive = currentTool?.path === tool.path
                      return (
                        <button
                          key={tool.path}
                          onClick={() => handleToolSwitch(tool.path)}
                          className={`
                            w-full flex items-center gap-3 px-3 py-2.5 text-[13px]
                            transition-colors duration-75
                            ${isActive
                              ? 'bg-linear-text-muted/15 text-linear-text'
                              : 'text-linear-text-secondary hover:bg-linear-text-muted/10 hover:text-linear-text'
                            }
                          `}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          <span className={isActive ? 'text-linear-accent' : 'text-linear-text-muted'}>{tool.icon}</span>
                          <span className="font-medium">{tool.name}</span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: User (Desktop) */}
        <div className="hidden md:flex items-center gap-2">
          {user && (
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-linear-text-muted/10 transition-colors"
                aria-expanded={profileMenuOpen}
                aria-haspopup="true"
                aria-label="User menu"
              >
                <div className="w-[22px] h-[22px] bg-linear-accent rounded flex items-center justify-center">
                  <span className="text-[10px] font-medium text-white">{getUserInitials()}</span>
                </div>
                <span className="text-[13px] text-linear-text-secondary hidden lg:inline max-w-[120px] truncate">
                  {user.name || user.email.split('@')[0]}
                </span>
                <svg
                  className={`w-3.5 h-3.5 text-linear-text-muted transition-transform duration-150 ${profileMenuOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {profileMenuOpen && (
                <div className="absolute right-0 mt-1 w-52 bg-linear-bg-card border border-linear-border-input rounded-lg shadow-lg overflow-hidden z-50 animate-scale-in">
                  <div className="px-3 py-2.5 border-b border-linear-border">
                    <p className="text-[13px] font-medium text-linear-text truncate">{user.name || user.email}</p>
                    <p className="text-[11px] text-linear-text-muted truncate mt-0.5">{user.email}</p>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        navigate('/settings')
                        setProfileMenuOpen(false)
                      }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-linear-text hover:bg-linear-text-muted/10 transition-colors"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
                        <circle cx="8" cy="8" r="2" />
                        <path d="M8 3v1M8 12v1M3 8h1M12 8h1M4.5 4.5l.7.7M10.8 10.8l.7.7M4.5 11.5l.7-.7M10.8 5.2l.7-.7" strokeLinecap="round" />
                      </svg>
                      <span>Settings</span>
                    </button>
                    <button
                      onClick={() => {
                        logout()
                        setProfileMenuOpen(false)
                      }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-error hover:bg-error/10 transition-colors"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
                        <path d="M6 2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2M10.5 11.5L14 8l-3.5-3.5M6 8h8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>Sign out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 -mr-2 text-linear-text-muted hover:text-linear-text transition-colors"
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle menu"
          >
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              {mobileMenuOpen ? (
                <path d="M5 5l10 10M5 15L15 5" strokeLinecap="round" />
              ) : (
                <path d="M3 5h14M3 10h14M3 15h14" strokeLinecap="round" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-linear-bg-sidebar border-t border-linear-border py-3 animate-fade-in">
          {showToolSwitcher && (
            <div className="mb-3">
              <p className="px-4 text-[11px] font-medium text-linear-text-muted uppercase tracking-wide mb-2">Tools</p>
              {tools.map((tool) => {
                const isActive = currentTool?.path === tool.path
                return (
                  <button
                    key={tool.path}
                    onClick={() => handleToolSwitch(tool.path)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-2.5 text-[13px]
                      transition-colors duration-75
                      ${isActive
                        ? 'bg-linear-text-muted/15 text-linear-text'
                        : 'text-linear-text-secondary hover:bg-linear-text-muted/10 hover:text-linear-text'
                      }
                    `}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span className={isActive ? 'text-linear-accent' : 'text-linear-text-muted'}>{tool.icon}</span>
                    <span className="font-medium">{tool.name}</span>
                  </button>
                )
              })}
            </div>
          )}

          {user && (
            <div className="border-t border-linear-border pt-3 mt-3">
              <div className="px-4 py-2">
                <p className="text-[13px] font-medium text-linear-text">{user.name || user.email}</p>
                <p className="text-[11px] text-linear-text-muted mt-0.5">{user.email}</p>
              </div>
              <button
                onClick={() => {
                  navigate('/settings')
                  setMobileMenuOpen(false)
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-linear-text hover:bg-linear-text-muted/10 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
                  <circle cx="8" cy="8" r="2" />
                  <path d="M8 3v1M8 12v1M3 8h1M12 8h1M4.5 4.5l.7.7M10.8 10.8l.7.7M4.5 11.5l.7-.7M10.8 5.2l.7-.7" strokeLinecap="round" />
                </svg>
                <span className="font-medium">Settings</span>
              </button>
              <button
                onClick={() => {
                  logout()
                  setMobileMenuOpen(false)
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-error hover:bg-error/10 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
                  <path d="M6 2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2M10.5 11.5L14 8l-3.5-3.5M6 8h8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="font-medium">Sign out</span>
              </button>
            </div>
          )}
        </div>
      )}
    </nav>
  )
}
